/**
 * Subjects that users can perform actions on.
 *
 * String values are a persisted contract for CASL and other stored subject
 * columns. They do not key `user_app_settings` (one opaque JSON document per
 * user). Adding members is free; renaming a value that is stored elsewhere
 * requires a data migration or those rows become unreachable.
 */
export enum AppSubjectEnum {
  /**
   * All
   * @description Reserved by CASL for `All` actions. Tenant-scope wildcard
   * once Phase 1c inverted rules are applied — it must not grant platform subjects.
   */
  ALL = 'all',

  /**
   * Platform wildcard
   * @description Ordinary subject string expanded at parse time to every
   * platform-scoped subject. Not a CASL universal wildcard.
   */
  PLATFORM_ALL = 'platform_all',

  // System
  SYSTEM = 'system',
  SETTINGS = 'settings',
  USER_SETTINGS = 'user_settings',
  USER_APP_SETTINGS = 'user_app_settings',
  APPEARANCE = 'appearance',
  POLICY = 'policy',
  USER = 'user',
  FILE = 'file',
  TAG = 'tag',
  AUDIT_LOG = 'audit_log',
  CHANGE_LOG = 'change_log',

  // Shared
  CONTACT = 'contact',
  DRIVER = 'driver',

  // Administration
  CUSTOMER = 'customer',
  LOCATION = 'location',
  SUPPLIER = 'supplier',
  AGENT = 'agent',
  AGENT_CATEGORY = 'agent_category',

  // Vehicles
  VEHICLE = 'vehicle',
  VEHICLE_FEATURE_ITEM = 'vehicle_feature_item',
  VEHICLE_FEATURE_ITEM_FILE = 'vehicle_feature_item_file',
  VEHICLE_GROUP = 'vehicle_group',
  VEHICLE_CATEGORY = 'vehicle_category',
  VEHICLE_CATEGORY_FEATURE_ITEM = 'vehicle_category_feature_item',
  VEHICLE_CATEGORY_FEATURE_ITEM_FILE = 'vehicle_category_feature_item_file',
  VEHICLE_CATEGORY_PRICING_TIER_ITEM = 'vehicle_category_pricing_tier_item',
  VEHICLE_DAMAGE = 'vehicle_damage',
  VEHICLE_GPS_TRACKING = 'vehicle_gps_tracking',
  VEHICLE_INSURANCE = 'vehicle_insurance',
  VEHICLE_INSURANCE_CLAIM = 'vehicle_insurance_claim',
  VEHICLE_MAINTENANCE = 'vehicle_maintenance',
  VEHICLE_ROAD_TAX = 'vehicle_road_tax',
  VEHICLE_ROAD_WORTHY_CHECK = 'vehicle_road_worthy_check',
  VEHICLE_COLOR = 'vehicle_color',
  VEHICLE_MAKE = 'vehicle_make',
  VEHICLE_MODEL = 'vehicle_model',

  // Operations
  RENTAL_AGREEMENT = 'rental_agreement',
  RENTAL_AGREEMENT_PARTY = 'rental_agreement_party',
  RENTAL_AGREEMENT_PARTY_DRIVING_LICENSE = 'rental_agreement_party_driving_license',
  RENTAL_RESERVATION = 'rental_reservation',
  RENTAL_RESERVATION_PARTY = 'rental_reservation_party',
  RENTAL_RESERVATION_PARTY_DRIVING_LICENSE = 'rental_reservation_party_driving_license',
  RENTAL_SNAPSHOT = 'rental_snapshot',
  AGREEMENT_CONTACT = 'agreement_contact',

  // Financials
  TRANSACTION = 'transaction',
  PAYMENT = 'payment',
  PAYMENT_ACTIVITY_TRACKING = 'payment_activity_tracking',

  // File Log
  FILE_LOG = 'file_log',

  // Notifications
  NOTIFICATION_PREFERENCES = 'notification_preferences',
  NOTIFICATION_LOG = 'notification_log',

  // Reminders
  REMINDER_PREFERENCES = 'reminder_preferences',
  REMINDER_LOG = 'reminder_log',

  // Proto Log
  PROTO_PREFERENCES = 'proto_preferences',
  PROTO_LOG = 'proto_log',

  // Reports
  REPORT = 'report',
  REPORT_TEMPLATE = 'report_template',
  REPORT_JOB = 'report_job',

  // Data Export
  DATA_EXPORT_JOB = 'data_export_job',

  // Tenants
  TENANT = 'tenant',
  TENANT_METADATA = 'tenant_metadata',
  INBOX = 'inbox',

  // Platform
  FILEBOX = 'filebox',
  OUTBOX = 'outbox',
  PLATFORM_SETTINGS = 'platform_settings',

  // Billing
  BILLABLE_EVENT = 'billable_event',
  ADDON = 'addon',
  STORAGE = 'storage',

  // Proto
  ACCOUNT = 'account',
  ASSET = 'asset',
  ASSET_FEATURE_ITEM = 'asset_feature_item',
  ASSET_PRICING_ITEM = 'asset_pricing_item',
  ASSET_RESERVATION = 'asset_reservation',
  TENANT_SETTINGS = 'tenant_settings',
}
